import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { adminGetUsersWithPhone } from "../lib/api";

interface UserWithPhone {
  _id: string;
  id?: string;
  username: string;
  email: string;
  phoneNumber: string;
  firstName?: string | null;
  lastName?: string | null;
  gender?: "male" | "female" | "other" | "prefer-not-to-say" | null;
  createdAt: string;
  lastActiveAt?: string | null;
  emailVerified?: boolean;
  isBanned?: boolean;
  isSuspended?: boolean;
  suspendedUntil?: string | null;
  roles?: string[];
  countryCode?: string;
}

type RoleFilter = "all" | "admin" | "moderator" | "vip" | "user";
type VerificationFilter = "all" | "verified" | "unverified";
type StatusFilter = "all" | "banned" | "suspended" | "normal";

export default function AdminUsersWithPhone() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [users, setUsers] = useState<UserWithPhone[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [verificationFilter, setVerificationFilter] =
    useState<VerificationFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const canUse = !!user?.isAdmin;

  useEffect(() => {
    if (canUse) {
      loadUsers();
    }
  }, [canUse]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await adminGetUsersWithPhone();
      
      // Backend might return { ok: true, users: [...] } or just [...]
      const userList = data.users || data;
      setUsers(Array.isArray(userList) ? userList : []);
    } catch (err: any) {
      console.error("Load users error:", err);
      setError(err.message || "Kullanıcılar yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  // Client-side filtering
  const filteredUsers = users.filter((user) => {
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchUsername = user.username.toLowerCase().includes(query);
      const matchEmail = user.email.toLowerCase().includes(query);
      const matchPhone = user.phoneNumber.toLowerCase().includes(query);
      const matchFirstName = user.firstName?.toLowerCase().includes(query);
      const matchLastName = user.lastName?.toLowerCase().includes(query);

      if (
        !matchUsername &&
        !matchEmail &&
        !matchPhone &&
        !matchFirstName &&
        !matchLastName
      ) {
        return false;
      }
    }

    // Role filter
    if (roleFilter !== "all") {
      const userRoles = user.roles || [];
      if (roleFilter === "admin" && !userRoles.includes("admin")) return false;
      if (roleFilter === "moderator" && !userRoles.includes("moderator"))
        return false;
      if (roleFilter === "vip" && !userRoles.includes("vip")) return false;
      if (
        roleFilter === "user" &&
        userRoles.some((r) => ["admin", "moderator", "vip"].includes(r))
      ) {
        return false;
      }
    }

    // Verification filter
    if (verificationFilter === "verified" && !user.emailVerified) return false;
    if (verificationFilter === "unverified" && user.emailVerified) return false;

    // Status filter
    if (statusFilter === "banned" && !user.isBanned) return false;
    if (statusFilter === "suspended" && !user.isSuspended) return false;
    if (
      statusFilter === "normal" &&
      (user.isBanned || user.isSuspended)
    )
      return false;

    return true;
  });

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTimeAgo = (dateString?: string | null): string => {
    if (!dateString) return "Hiç";

    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) return `${diffMins} dakika önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;
    return formatDate(dateString);
  };

  const formatGender = (
    gender?: "male" | "female" | "other" | "prefer-not-to-say" | null
  ): string => {
    switch (gender) {
      case "male":
        return "Erkek";
      case "female":
        return "Kadın";
      case "other":
        return "Diğer";
      case "prefer-not-to-say":
        return "Belirtmem";
      default:
        return "Belirtilmemiş";
    }
  };

  const getRoleBadges = (roles?: string[]) => {
    if (!roles || roles.length === 0) {
      return (
        <span
          style={{
            fontSize: 11,
            padding: "3px 8px",
            borderRadius: 6,
            background: "#374151",
            color: "#9ca3af",
            fontWeight: 600,
          }}
        >
          User
        </span>
      );
    }

    return roles.map((role) => {
      let bgColor = "#374151";
      let textColor = "#9ca3af";

      if (role === "admin") {
        bgColor = "#fbbf24";
        textColor = "#78350f";
      } else if (role === "moderator") {
        bgColor = "#8b5cf6";
        textColor = "#fff";
      } else if (role === "vip") {
        bgColor = "#f97316";
        textColor = "#fff";
      }

      return (
        <span
          key={role}
          style={{
            fontSize: 11,
            padding: "3px 8px",
            borderRadius: 6,
            background: bgColor,
            color: textColor,
            fontWeight: 600,
            marginRight: 4,
          }}
        >
          {role.toUpperCase()}
        </span>
      );
    });
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg)",
        color: "var(--text)",
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 24,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: 32,
                fontWeight: 900,
                margin: 0,
                marginBottom: 8,
              }}
            >
              📱 Telefon Numaralı Kullanıcılar
            </h1>
            <p
              style={{
                fontSize: 14,
                color: "var(--muted)",
                margin: 0,
              }}
            >
              Kişisel bilgilerinde telefon numarası kayıtlı kullanıcılar
            </p>
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <button
              onClick={loadUsers}
              disabled={loading}
              style={{
                padding: "10px 20px",
                background: "var(--primary)",
                border: "none",
                borderRadius: 8,
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span style={{ fontSize: 16 }}>🔄</span>
              Yenile
            </button>
            <button
              onClick={() => navigate("/settings")}
              style={{
                padding: "10px 20px",
                background: "transparent",
                border: "1px solid var(--muted)",
                borderRadius: 8,
                color: "var(--text)",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              ← Geri
            </button>
          </div>
        </div>

        {/* Stats */}
        {!loading && !error && (
          <div
            style={{
              background: "rgba(59, 130, 246, 0.1)",
              border: "1px solid rgba(59, 130, 246, 0.3)",
              borderRadius: 12,
              padding: 16,
              marginBottom: 24,
            }}
          >
            <p
              style={{
                margin: 0,
                fontSize: 14,
                color: "#93c5fd",
                textAlign: "center",
              }}
            >
              📊 Toplam <strong>{users.length}</strong> kullanıcı • Filtreli
              sonuç: <strong>{filteredUsers.length}</strong>
            </p>
          </div>
        )}

        {/* Filters */}
        <div
          style={{
            background: "var(--bg2)",
            border: "1px solid var(--muted)",
            borderRadius: 16,
            padding: 24,
            marginBottom: 24,
          }}
        >
          <h3
            style={{
              fontSize: 16,
              fontWeight: 700,
              margin: 0,
              marginBottom: 16,
            }}
          >
            🔍 Filtreler ve Arama
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
              gap: 16,
            }}
          >
            {/* Search */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: "var(--muted)",
                }}
              >
                Arama
              </label>
              <input
                type="text"
                placeholder="Kullanıcı adı, email veya telefon..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  padding: 10,
                  background: "var(--bg)",
                  border: "1px solid var(--muted)",
                  borderRadius: 8,
                  color: "var(--text)",
                  fontSize: 14,
                }}
              />
            </div>

            {/* Role Filter */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: "var(--muted)",
                }}
              >
                Rol
              </label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as RoleFilter)}
                style={{
                  width: "100%",
                  padding: 10,
                  background: "var(--bg)",
                  border: "1px solid var(--muted)",
                  borderRadius: 8,
                  color: "var(--text)",
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                <option value="all">Tümü</option>
                <option value="admin">Admin</option>
                <option value="moderator">Moderator</option>
                <option value="vip">VIP</option>
                <option value="user">Normal User</option>
              </select>
            </div>

            {/* Verification Filter */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: "var(--muted)",
                }}
              >
                Email Doğrulama
              </label>
              <select
                value={verificationFilter}
                onChange={(e) =>
                  setVerificationFilter(e.target.value as VerificationFilter)
                }
                style={{
                  width: "100%",
                  padding: 10,
                  background: "var(--bg)",
                  border: "1px solid var(--muted)",
                  borderRadius: 8,
                  color: "var(--text)",
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                <option value="all">Tümü</option>
                <option value="verified">Doğrulanmış</option>
                <option value="unverified">Doğrulanmamış</option>
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: "var(--muted)",
                }}
              >
                Hesap Durumu
              </label>
              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as StatusFilter)
                }
                style={{
                  width: "100%",
                  padding: 10,
                  background: "var(--bg)",
                  border: "1px solid var(--muted)",
                  borderRadius: 8,
                  color: "var(--text)",
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                <option value="all">Tümü</option>
                <option value="normal">Normal</option>
                <option value="banned">Banlı</option>
                <option value="suspended">Askıda</option>
              </select>
            </div>
          </div>

          {/* Clear Filters */}
          {(searchQuery || roleFilter !== "all" || verificationFilter !== "all" || statusFilter !== "all") && (
            <button
              onClick={() => {
                setSearchQuery("");
                setRoleFilter("all");
                setVerificationFilter("all");
                setStatusFilter("all");
              }}
              style={{
                marginTop: 16,
                padding: "8px 16px",
                background: "transparent",
                border: "1px solid var(--muted)",
                borderRadius: 8,
                color: "var(--muted)",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              🗑️ Filtreleri Temizle
            </button>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: 60,
            }}
          >
            <div
              style={{
                width: 48,
                height: 48,
                border: "4px solid rgba(59, 130, 246, 0.3)",
                borderTop: "4px solid #3b82f6",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ marginTop: 16, color: "var(--muted)", fontSize: 14 }}>
              Kullanıcılar yükleniyor...
            </p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div
            style={{
              background: "rgba(239, 68, 68, 0.1)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              borderRadius: 12,
              padding: 24,
              textAlign: "center",
            }}
          >
            <p style={{ fontSize: 32, margin: 0, marginBottom: 12 }}>⚠️</p>
            <p
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: "#ef4444",
                margin: 0,
                marginBottom: 16,
              }}
            >
              {error}
            </p>
            <button
              onClick={loadUsers}
              style={{
                padding: "10px 20px",
                background: "#ef4444",
                border: "none",
                borderRadius: 8,
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Yeniden Dene
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && filteredUsers.length === 0 && (
          <div
            style={{
              background: "var(--bg2)",
              border: "1px solid var(--muted)",
              borderRadius: 16,
              padding: 60,
              textAlign: "center",
            }}
          >
            <p style={{ fontSize: 48, margin: 0, marginBottom: 16 }}>
              {users.length === 0 ? "📱" : "🔍"}
            </p>
            <h3
              style={{
                fontSize: 18,
                fontWeight: 700,
                margin: 0,
                marginBottom: 8,
              }}
            >
              {users.length === 0
                ? "Henüz telefon numarası yok"
                : "Eşleşen kullanıcı bulunamadı"}
            </h3>
            <p
              style={{
                fontSize: 14,
                color: "var(--muted)",
                margin: 0,
                lineHeight: 1.6,
              }}
            >
              {users.length === 0
                ? "Kullanıcılar kişisel bilgilerinde telefon numarası eklediklerinde burada görünecekler."
                : "Farklı filtre seçenekleri deneyin veya arama teriminizi değiştirin."}
            </p>
          </div>
        )}

        {/* Users Table */}
        {!loading && !error && filteredUsers.length > 0 && (
          <div
            style={{
              background: "var(--bg2)",
              border: "1px solid var(--muted)",
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  minWidth: 900,
                }}
              >
                <thead>
                  <tr
                    style={{
                      background: "rgba(255,255,255,0.03)",
                      borderBottom: "1px solid var(--muted)",
                    }}
                  >
                    <th
                      style={{
                        padding: 16,
                        textAlign: "left",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--muted)",
                      }}
                    >
                      Kullanıcı
                    </th>
                    <th
                      style={{
                        padding: 16,
                        textAlign: "left",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--muted)",
                      }}
                    >
                      Telefon
                    </th>
                    <th
                      style={{
                        padding: 16,
                        textAlign: "left",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--muted)",
                      }}
                    >
                      Durum
                    </th>
                    <th
                      style={{
                        padding: 16,
                        textAlign: "left",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--muted)",
                      }}
                    >
                      Aktivite
                    </th>
                    <th
                      style={{
                        padding: 16,
                        textAlign: "left",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--muted)",
                      }}
                    >
                      Roller
                    </th>
                    <th
                      style={{
                        padding: 16,
                        textAlign: "center",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "var(--muted)",
                      }}
                    >
                      İşlemler
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr
                      key={user._id || user.id}
                      style={{
                        borderBottom: "1px solid rgba(255,255,255,0.05)",
                      }}
                    >
                      {/* User Info */}
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <div
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              background: "var(--primary)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 18,
                              fontWeight: 700,
                              color: "#fff",
                              flexShrink: 0,
                            }}
                          >
                            {user.username.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: 15,
                                fontWeight: 600,
                                color: "var(--primary)",
                                marginBottom: 2,
                                cursor: "pointer",
                              }}
                              onClick={() => navigate(`/u/${user.username}`)}
                            >
                              @{user.username}
                            </div>
                            {(user.firstName || user.lastName) && (
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "var(--text)",
                                  marginBottom: 2,
                                }}
                              >
                                {user.firstName} {user.lastName}
                              </div>
                            )}
                            <div
                              style={{
                                fontSize: 11,
                                color: "var(--muted)",
                              }}
                            >
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Phone */}
                      <td style={{ padding: 16 }}>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>
                          📞 {user.phoneNumber}
                        </div>
                        {user.gender && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "var(--muted)",
                              marginTop: 4,
                            }}
                          >
                            👤 {formatGender(user.gender)}
                          </div>
                        )}
                      </td>

                      {/* Status */}
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {/* Email Verification */}
                          <div style={{ fontSize: 12 }}>
                            {user.emailVerified ? (
                              <span style={{ color: "#10b981" }}>
                                ✅ Verified
                              </span>
                            ) : (
                              <span style={{ color: "#fbbf24" }}>
                                ⚠️ Unverified
                              </span>
                            )}
                          </div>

                          {/* Ban Status */}
                          {user.isBanned && (
                            <div style={{ fontSize: 12, color: "#ef4444" }}>
                              ⛔ Banlı
                            </div>
                          )}

                          {/* Suspend Status */}
                          {user.isSuspended && (
                            <div style={{ fontSize: 12, color: "#f97316" }}>
                              ⏸️ Askıda
                              {user.suspendedUntil && (
                                <div style={{ fontSize: 10, color: "var(--muted)" }}>
                                  {formatDate(user.suspendedUntil)}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Normal */}
                          {!user.isBanned && !user.isSuspended && (
                            <div style={{ fontSize: 12, color: "#10b981" }}>
                              ✅ Normal
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Activity */}
                      <td style={{ padding: 16 }}>
                        <div style={{ fontSize: 12, color: "var(--muted)" }}>
                          <div style={{ marginBottom: 4 }}>
                            📅 Kayıt: {formatDate(user.createdAt)}
                          </div>
                          <div>
                            🕐 Son aktif: {getTimeAgo(user.lastActiveAt)}
                          </div>
                        </div>
                      </td>

                      {/* Roles */}
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {getRoleBadges(user.roles)}
                        </div>
                      </td>

                      {/* Actions */}
                      <td style={{ padding: 16, textAlign: "center" }}>
                        <button
                          onClick={() => navigate(`/u/${user.username}`)}
                          style={{
                            padding: "6px 12px",
                            background: "var(--primary)",
                            border: "none",
                            borderRadius: 6,
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: "pointer",
                          }}
                        >
                          ➜ Profili Aç
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Spinner Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
